import { DonationFormType } from "../cms/modernBlocks/blocks/donationFormBlock/donationFormBlock.state"
import { sitename } from "../globals"
import { donationDataLayerServices } from "./datalayer.donation"

export type WindowWithDatalayer = {
  dataLayer: {
    push: (args: datalayerObject) => void
  }
}

export type datalayerObject = {
  event?: string
  category?: string
  action?: string
  label?: string
  click_url?: string
  click_text?: string
  articleNumber?: string
  gender?: string
  progress?: string
  flow?: string
  amount?: string
  "email_opt-in"?: string
  "telefoon_opt-in"?: string
} | GtmCheckoutEvent | GtmTransactionEvent | GtmPropositionPageEvent

const cookiesDatalayer = (accepted: boolean) =>
  pushDatalayer({
    event: 'cookies',
    category: 'cookie consent',
    action: accepted ? 'true' : 'false',
    label: accepted ? '1000' : '-100'
  })

const downloadDataLayer = (kind: 'shop' | 'download', label: string, articleNumber?: string) =>
  pushDatalayer({
    event: kind == 'download' ? 'download' : 'download_shop',
    category: kind == 'download' ? 'file_download' : 'downloads',
    action: label.slice(label.lastIndexOf(".") + 1),
    label: label,
    articleNumber: kind == 'download' ? '' : articleNumber ? articleNumber : ''
  })

const newsletterDataLayer = (opt: boolean, label: string, gender?: string) =>
  pushDatalayer({
    event: 'newsletter',
    category: 'newsletter',
    action: opt ? 'opt-in' : 'subscription',
    label: label,
    gender: gender ? (gender == 'vrouw' ? 'F' : 'M') : 'N/A'
  })

const telemarketingDataLayer = (label: string) =>
  pushDatalayer({
    event: 'telemarketing',
    category: 'telemarketing',
    action: 'subscription',
    label: label
  })

const videoDataLayer = (label: string, type: 'video_start' | 'video_progress' | 'video_interactions' | 'video_complete', progress?: number) =>
  pushDatalayer({
    event: 'video_interactions',
    category: 'video',
    action: type,
    label: label,
    progress: progress ? (progress == 25 || progress == 50 || progress || 75 || progress == 90) ? `${progress.toString()}%` : undefined : undefined
  })

const tagsDataLayer = (action: string) => (
  pushDatalayer({
    event: 'tags',
    category: 'tags',
    action: action,
    label: ''
  })
)

const flyoutDataLayer = (action: string) => (
  pushDatalayer({
    event: 'flyout',
    category: 'fly_out',
    action: action,
    label: 'open'
  })
)

const hartleftijdDataLayer = (action: string, label: string) => (
  pushDatalayer({
    event: 'hartleftijd',
    category: 'hartleftijd',
    action: action,
    label: label
  })
)

const buttonLinkClickDataLayer = (click_url: string, click_text: string) => (
  pushDatalayer({
    event: 'button_link_click',
    click_url: click_url,
    click_text: click_text,
  })
)

type Step1CheckoutActionField = {
  step: 1 | 2 | 3
  donationFrequency: string
}

type Step2CheckoutActionField = {
  step: 4
  donationFrequency: string
  'opt-in': 'no'
}

export type GtmProduct = {
  name?: string
  id?: string
  price?: string
  variant?: string
  brand?: string
  category?: string
  quantity?: string
}

type CheckoutActionField = Step1CheckoutActionField | Step2CheckoutActionField

type GtmCheckoutEvent = {
  event: 'checkout'
  ecommerce: {
    checkout: {
      actionField: CheckoutActionField
      products: GtmProduct[]
    }
  },
  events: {
    category: string,
    action: string,
    label: DonationFormType,
  },
}

type GtmTransactionEvent = {
  event: 'transaction'
  ecommerce: {
    currencyCode: string
    purchase: {
      actionField: {
        id: string
        revenue: string
        donationFrequency: string
        'opt-in': string
      }
      products: GtmProduct[]
    }
  }
  events: {
    category: 'ecommerce'
    action: 'transaction'
  }
}

type GtmPropositionPageEvent = {
  event: 'donation_blocks',
  flow: string,
  amount?: string
  package?: string
  page_path?: string
  "email_opt-in"?: string
}

const hamburgerMenuDataLayer = (action: 'open' | 'close') => (
  pushDatalayer({
    event: 'menu',
    category: 'menu',
    action: action,
    label: ''
  })
)

const menuItemDataLayer = (action: string, label: string) => (
  pushDatalayer({
    event: 'menu',
    category: 'menu',
    action: action,
    label: label
  })
)

const beroerteDataLayer = (action: 'Button klik' | 'Antwoord klik' | 'Resultaat getoond' | 'Openklapper' | 'Dichtklapper', label: string) => (
  pushDatalayer({
    event: 'beroertetest',
    category: `Beroertetest: variant video's met situatieschets in tekst`,
    action: action,
    label: label
  })
)

const bloeddrukquizDataLayer = (action: 'Button klik' | 'Antwoord klik' | 'Resultaat getoond' | 'Openklapper' | 'Dichtklapper', label: string) => (
  pushDatalayer({
    event: 'bloeddrukquiz',
    category: 'Test je kennis over bloeddruk',
    action: action,
    label: label
  })
)

const postcodeDataLayer = (action: string, label: string) => (
  pushDatalayer({
    event: 'postcodechecker',
    category: 'Postcodechecker',
    action: action,
    label: label
  })
)

const expandableDataLayer = (action: string) => (
  pushDatalayer({
    event: 'flyout',
    category: 'fly_out',
    action: action,
    label: "open"
  })
)

const newDataLayerEventAction = (action: string) => (
  pushDatalayer({
    event: 'donation-form',
    flow: `stap 1 - ${action}`
  })
)

const newDataLayerEventAmount = (amount: string) => (
  pushDatalayer({
    event: 'donation-form',
    amount: amount
  })
)

const newDataLayerEventButton = (step: string, text: string) => (
  pushDatalayer({
    event: 'donation-form',
    flow: `stap ${step} - button ${text}`
  })
)

const newDataLayerEventInteraction = () => (
  pushDatalayer({
    event: 'donation-form',
    flow: 'stap 2 - interactie'
  })
)

export const pushDatalayer = (d: datalayerObject) => {
  const defaultParams = {
    event: `event.${sitename}`,
    label: window.location.pathname
  }

  const params = { ...defaultParams, ...d }
  window.dataLayer ? window.dataLayer.push(params) : console.log(params)
}

export const dataLayerServices = {
  ...donationDataLayerServices,
  newDataLayerEventAction,
  newDataLayerEventAmount,
  newDataLayerEventButton,
  newDataLayerEventInteraction,
  cookiesDatalayer,
  downloadDataLayer,
  newsletterDataLayer,
  telemarketingDataLayer,
  videoDataLayer,
  buttonLinkClickDataLayer,
  hamburgerMenuDataLayer,
  menuItemDataLayer,
  beroerteDataLayer,
  bloeddrukquizDataLayer,
  postcodeDataLayer,
  tagsDataLayer,
  flyoutDataLayer,
  hartleftijdDataLayer,
  expandableDataLayer,
}